/**
 * Uploads travel as a base64 data URL inside a JSON body (see FilesApi.upload), and
 * nginx in front of the API caps request bodies at 1MB. Base64 inflates bytes by ~4/3,
 * so an untouched ~750KB photo already overflows that cap — nginx then answers 413
 * without CORS headers and the browser reports it as an opaque CORS failure. Shrinking
 * here keeps every upload comfortably inside the cap.
 */

/** Longest edge kept after downscaling. Covers full-width hero images without waste. */
const MAX_DIMENSION = 1600;
/** Smallest longest-edge we will shrink to before giving up. */
const MIN_DIMENSION = 320;
/** Budget for the finished data URL, well under the 1MB body cap once JSON is added. */
export const MAX_ENCODED_LENGTH = 800 * 1024;
/** Largest source file accepted, matching the "up to 5MB" hint shown in the dropzone. */
export const MAX_SOURCE_BYTES = 5 * 1024 * 1024;

const JPEG_QUALITIES = [0.85, 0.75, 0.65, 0.55, 0.45];

export class ImageTooLargeError extends Error {}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Image could not be decoded'));
    };
    img.src = url;
  });
}

function drawScaled(img: HTMLImageElement, maxDimension: number): HTMLCanvasElement {
  const longest = Math.max(img.naturalWidth, img.naturalHeight) || 1;
  const scale = Math.min(1, maxDimension / longest);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
}

/**
 * Re-encoding a transparent logo as JPEG turns its background black, so those stay PNG
 * and are shrunk by dimension alone. Everything else becomes JPEG, which compresses
 * photographs far better than PNG.
 */
function hasTransparency(canvas: HTMLCanvasElement): boolean {
  const ctx = canvas.getContext('2d');
  if (!ctx) return false;
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) return true;
  }
  return false;
}

export type CompressedImage = {
  /** Data URL, ready to send as the `file` field. */
  dataUrl: string;
  /** Extension matching the encoded bytes — the API derives the stored mime type from it. */
  extension: 'jpg' | 'png';
};

/**
 * Downscales and re-encodes `file` until its data URL fits MAX_ENCODED_LENGTH.
 *
 * @throws ImageTooLargeError when even a MIN_DIMENSION render stays over budget.
 */
export async function compressImage(file: File): Promise<CompressedImage> {
  const img = await loadImage(file);
  const keepAlpha = file.type === 'image/png' && hasTransparency(drawScaled(img, MIN_DIMENSION));
  const extension = keepAlpha ? 'png' : 'jpg';

  for (let dimension = MAX_DIMENSION; dimension >= MIN_DIMENSION; dimension = Math.round(dimension / 2)) {
    const canvas = drawScaled(img, dimension);

    if (keepAlpha) {
      const dataUrl = canvas.toDataURL('image/png');
      if (dataUrl.length <= MAX_ENCODED_LENGTH) return { dataUrl, extension };
      continue;
    }

    for (const quality of JPEG_QUALITIES) {
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      if (dataUrl.length <= MAX_ENCODED_LENGTH) return { dataUrl, extension };
    }
  }

  throw new ImageTooLargeError('Image could not be compressed under the upload limit');
}
